import { useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { ApolloError } from '@apollo/client'

import {
  RequestQuery,
  useMachinesSelectQuery,
  useSetRfqAccountMachineMutation,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useMsgs from '@/gf/hooks/useMsgs'

import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import BigSelect from '@/gf/components/BigSelect'
import Form from '@/gf/components/Form'
import Modal from '@/gf/components/ModalNext'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

type ContextType = {
  rfq: RequestQuery['requestForQuotesSearch']['requestForQuotes'][number]
  refetch: () => Promise<unknown>
}

const EditMachine = () => {
  const { orgId } = useSession()
  const { rfq, refetch: refetchRfq } = useOutletContext<ContextType>()
  const [_, msgr] = useMsgs()
  const navigate = useNavigate()
  const gqlClient = useGqlClient()
  const [machineId, setMachineId] = useState(rfq.orgMachines.at(0)?.id || null)
  const [performing, setPerforming] = useState(false)
  const [setRfqAccountMachine] = useSetRfqAccountMachineMutation({ client: gqlClient })

  const { data, refetch, loading } = useMachinesSelectQuery({
    variables: { orgId, value: null, id: machineId },
    client: useGqlClient(),
    notifyOnNetworkStatusChange: true,
  })

  const close = () => navigate(`/rfqs/${rfq.id}/request`)

  const options = data?.machinesSearch.map((m) => ({
    value: m.id,
    label: `${m.name}, ${m.machine.make}, ${m.machine.model}, ${m.serialNumber}`,
  }))

  const storeOrderCount = rfq.storeOrders.length

  const onSubmit = () => {
    setPerforming(true)

    setRfqAccountMachine({
      variables: { rfqId: rfq.id, orgMachineId: machineId },
    })
      .then(() => {
        close()
        msgr.add('Machine saved.', 'positive')
        refetchRfq()
      })
      .catch((err: ApolloError) => {
        const message = err.graphQLErrors?.[0]?.message || err.networkError?.message || err.message

        if (message) {
          msgr.add(message, 'negative')
        } else {
          msgr.addUnknownError()
        }
      })
      .finally(() => setPerforming(false))
  }

  return (
    <Modal onClose={close} overflowHidden={false}>
      <Form onSubmit={onSubmit} className="p-6 space-y-4">
        <div className="text-xl">Edit Machine</div>

        <div>
          <BigSelect
            options={options}
            filter={(term) => refetch({ value: term, id: null })}
            loading={loading}
            selectedValue={machineId}
            setSelectedValue={setMachineId}
          />
        </div>

        {storeOrderCount > 0 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <span>
              Changing the machine will also update {storeOrderCount}{' '}
              {storeOrderCount === 1 ? 'order' : 'orders'}.
            </span>
          </div>
        )}

        <div className="flex gap-2 flex-row-reverse">
          <Action.P type="submit" performing={performing}>
            Save
          </Action.P>

          <Action.S onClick={close}>Cancel</Action.S>
        </div>
      </Form>
    </Modal>
  )
}

export default EditMachine
